import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import styled from 'styled-components';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {CustomError} from '../../../libs/shared/helpers/src/types/customErrors';
import {TextBox} from '../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {useConstant} from '../../../srcCommon/helpers/hookHelpers';
import {Dialog} from '../../components/common/dialogs/dialog';
import {DialogButtonGroup} from '../../components/common/dialogs/dialogButtonGroup';
import {DialogHeader} from '../../components/common/dialogs/dialogHeader';
import {AnswerFolderDropdownBox} from '../../components/common/dropdowns/answerFolders/answerFolderDropdownBox';
import {ResolveDispatchProps, useAppSelector} from '../../helpers/reduxHelpers';
import {makeMapDispatchToProps} from '../../helpers/reselectHelpers';
import {AnswerOrFolderModel, RAnswerFolder} from '../../models/api/answerFolderModel';
import {AnswerFolderNameAlreadyExistsError} from '../../store/data/answerFolders/answerFolderActions';
import {makeGetAnswerFolder} from '../../store/data/answerFolders/answerFolderSelectors';
import {
  AnswerFolderTemplate,
  createAnswerFolderAsyncAction,
} from '../../store/data/answerFolders/saveFolderActions';
import {moveAnswersOrFolderBulkAsync} from '../../store/data/answers/answerBulkActions';

/*
 * Props.
 */

interface AddAnswerFolderDialogContainerProps {
  onRequestClose: Handler;
  itemsToActOn: ReadonlyArray<AnswerOrFolderModel>;
  namespace: string;
  onCreate: Handler;
  prefilledFolderId?: number;
}

/*
 * Style.
 */

const StyledDialogContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  margin: 20px;
`;

/*
 * Component.
 */

interface AddAnswerFolderDialogContainerComponentProps
  extends AddAnswerFolderDialogContainerProps,
    ResolveDispatchProps<typeof mapDispatchToProps> {}

const AddAnswerFolderDialogContainerComponent: FC<AddAnswerFolderDialogContainerComponentProps> = (props) => {
  const initialFolderState = useConstant(() => ({
    name: '',
    parentFolderId: props.prefilledFolderId,
    namespace: props.namespace,
  }));

  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState(initialFolderState.name);
  const [folderId, setFolderId] = useState<number | undefined>(initialFolderState.parentFolderId);
  const [error, setError] = useState<CustomError | undefined>(undefined);
  const {namespace} = props;

  const getSelectedFolder = useMemo(() => makeGetAnswerFolder(() => folderId), [folderId]);
  const selectedFolder = useAppSelector(getSelectedFolder);
  const canSave = useMemo(
    () => isDirty && !isSaving && Boolean(name && namespace),
    [isDirty, isSaving, name, namespace],
  );

  const onOkClick = async () => {
    try {
      setError(undefined);
      setIsSaving(true);
      setIsDirty(false);

      const template: AnswerFolderTemplate = {
        name,
        namespace,
        folderId,
      };

      const newFolder = await props.createAnswerFolder(template);
      const [foldersToActOn, answersToActOn] = _(props.itemsToActOn)
        .partition((i) => RAnswerFolder.is(i))
        .value();

      await props.moveAnswersOrFolderBulk(
        namespace,
        newFolder.id,
        foldersToActOn.map((i) => i.id),
        answersToActOn.map((i) => i.id),
      );
      props.onCreate();
    } catch (caughtError) {
      setIsSaving(false);
      setIsDirty(true);

      if (caughtError instanceof CustomError) {
        setError(caughtError);
      } else {
        throw caughtError;
      }
    }
  };

  return (
    <Dialog width={300} onRequestClose={props.onRequestClose}>
      <DialogHeader title={<Trans id="addanswerfolder-title">Create new folder</Trans>} />
      <StyledDialogContent>
        <TextBox
          label={<Trans id="foldereditor-name">Name</Trans>}
          value={name}
          onChange={(newName) => {
            setIsDirty(true);
            setName(newName);
          }}
          isRequired
          shouldAutoFocus
          errorMessage={
            error instanceof AnswerFolderNameAlreadyExistsError && (
              <Trans id="answer-folder-create-name-exist-error">This name is already used</Trans>
            )
          }
          hasError={error !== undefined}
        />
        <AnswerFolderDropdownBox
          shouldHideTooltipIcon
          onFolderSelect={(newFolderId) => {
            setFolderId(newFolderId);
          }}
          selectedFolderId={folderId}
          selectedFolder={selectedFolder}
          allAvailableNamespaces={[props.namespace]}
        />
      </StyledDialogContent>
      <DialogButtonGroup>
        <ButtonGroupSpacer />
        <Button onClick={props.onRequestClose}>
          <Trans id="settingstags-cancel">Cancel</Trans>
        </Button>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onOkClick} isDisabled={!canSave}>
          <Trans id="settingstags-createtag">Create</Trans>
        </Button>
      </DialogButtonGroup>
    </Dialog>
  );
};

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  moveAnswersOrFolderBulk: moveAnswersOrFolderBulkAsync,
  createAnswerFolder: createAnswerFolderAsyncAction,
}));

export const AddAnswerFolderDialogContainer = connect(
  null,
  mapDispatchToProps,
)(AddAnswerFolderDialogContainerComponent);
